import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Language, LANGUAGES, MATERIAL_MAX_CHARS, MAX_PHOTOS } from '@quizdb/common';
import { Icon } from '../common/Icon';
import {
  MaterialPrepError,
  MaterialPrepErrorCode,
  OCR_WORKERS,
} from './extraction/extractionTypes';
import { hashFile } from './extraction/hashFile';
import { CreateOcr, OcrEngine } from './ocr/ocrTypes';
import { createTesseractOcr } from './ocr/tesseractOcr';
import { isAcceptedPhoto, sortPhotosByName } from './photo/photoSet';
import { preparePhotoImage } from './photo/preparePhotoImage';
import {
  PHOTO_ACCEPT,
  Photo,
  PhotoMaterialSectionProps,
  PhotoRotation,
  PhotoSet,
} from './photo/photoTypes';

/** What the section reads photos with; tests pass fakes. */
export interface PhotoSectionDeps {
  createOcr: CreateOcr;
  preparePhotoImage: typeof preparePhotoImage;
  hash: (file: Blob) => Promise<string>;
}

const defaultDeps: PhotoSectionDeps = {
  createOcr: createTesseractOcr,
  preparePhotoImage,
  hash: hashFile,
};

/** The characters of a set's ready photos, which Start holds to `MATERIAL_MAX_CHARS` (PHO-D5). */
export const photoSetChars = (set: PhotoSet): number =>
  set.photos.reduce(
    (sum, photo) => sum + (photo.status === 'ready' ? (photo.text?.length ?? 0) : 0),
    0
  );

/** A photo to read, with the token it was queued under. */
interface ReadJob {
  id: string;
  token: number;
}

/** The read queue of one mount; a read from an earlier mount finds it replaced and stops. */
interface Scheduler {
  queue: ReadJob[];
  inFlight: number;
}

const noop = () => undefined;

/** A photo about to be read again: its text and error go, its hash stays. */
const toReading = (photo: Photo): Photo => ({
  ...photo,
  status: 'reading',
  text: undefined,
  error: undefined,
});

/**
 * New Job's page photos (QZ-D91): a set of photos, each read by OCR on its own in this browser
 * in the page language (PHO-D1), shown as numbered thumbnails the admin reorders, rotates and
 * removes (PHO-D3, PHO-D4). At most `OCR_WORKERS` photos are read at once (PHO-D9), all on one
 * engine for the page language, terminated when the language changes or the section unmounts
 * (PHO-D10). The set is named by `MaterialNameFields`'s document, not here (QZ-D100).
 */
export const PhotoMaterialSection = ({
  value,
  onChange,
  pageLanguage,
  onPageLanguageChange,
  error,
  disabled,
  deps = defaultDeps,
}: PhotoMaterialSectionProps & { deps?: PhotoSectionDeps }) => {
  const { t, i18n } = useTranslation();

  // Reads finish asynchronously, so they apply their result to the latest set, never to the one
  // captured when they started. The ref follows the prop only when the prop itself changes: a
  // re-render of this section with the old prop, before the parent has applied our last
  // `onChange`, must not roll back a set we have already sent.
  const valueRef = useRef(value);
  const lastPropRef = useRef(value);
  if (value !== lastPropRef.current) {
    lastPropRef.current = value;
    valueRef.current = value;
  }
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const pageLanguageRef = useRef(pageLanguage);
  pageLanguageRef.current = pageLanguage;

  // Bumped per photo on each queueing, so only its latest read lands.
  const tokensRef = useRef(new Map<string, number>());
  const schedulerRef = useRef<Scheduler | undefined>(undefined);
  const engineRef = useRef<{ promise: Promise<OcrEngine> } | undefined>(undefined);
  // Bumped whenever the engine is let go, so one created for an old language or an old mount is
  // terminated the moment it arrives and never used (PHO-D10).
  const engineGenerationRef = useRef(0);
  // The language the photos were last queued in; the effect below re-reads when it changes.
  const queuedLanguageRef = useRef(pageLanguage);
  const urlsRef = useRef(new Map<string, string>());
  const [, setUrlsVersion] = useState(0);

  const [addErrors, setAddErrors] = useState<MaterialPrepErrorCode[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const commit = (next: PhotoSet) => {
    valueRef.current = next;
    onChangeRef.current(next);
  };

  /** Gives photo `id` these fields in the latest set, if it is still there. */
  const updatePhoto = (id: string, fields: Partial<Photo>) => {
    const current = valueRef.current;
    if (!current.photos.some((photo) => photo.id === id)) {
      return;
    }
    commit({
      ...current,
      photos: current.photos.map((photo) => (photo.id === id ? { ...photo, ...fields } : photo)),
    });
  };

  const releaseEngine = () => {
    engineGenerationRef.current++;
    const entry = engineRef.current;
    engineRef.current = undefined;
    entry?.promise.then((engine) => engine.terminate()).catch(noop);
  };

  /** The page language's engine, created on the first read and shared by all (PHO-D10). */
  const getEngine = (): Promise<OcrEngine> => {
    if (!engineRef.current) {
      const generation = engineGenerationRef.current;
      const promise = depsRef.current.createOcr(pageLanguageRef.current).then(
        (engine) => {
          if (engineGenerationRef.current !== generation) {
            engine.terminate().catch(noop);
            throw new DOMException('The engine was let go.', 'AbortError');
          }
          return engine;
        },
        (thrown: unknown) => {
          throw thrown instanceof MaterialPrepError
            ? thrown
            : new MaterialPrepError('OcrUnavailable');
        }
      );
      const entry = { promise };
      engineRef.current = entry;
      // A failed creation is tried again by the next read.
      promise.catch(() => {
        if (engineRef.current === entry) {
          engineRef.current = undefined;
        }
      });
    }
    return engineRef.current.promise;
  };

  const readPhoto = async (scheduler: Scheduler, job: ReadJob) => {
    const findPhoto = () => valueRef.current.photos.find((photo) => photo.id === job.id);
    const isMounted = () => schedulerRef.current === scheduler;
    const isCurrent = () =>
      isMounted() && tokensRef.current.get(job.id) === job.token && !!findPhoto();

    const photo = findPhoto();
    if (!photo || !isCurrent()) {
      return;
    }
    try {
      if (photo.sha256 === undefined) {
        const sha256 = await depsRef.current.hash(photo.file);
        // The hash is the file's, so it holds for any later read of this photo too.
        if (isMounted()) {
          updatePhoto(job.id, { sha256 });
        }
        if (!isCurrent()) {
          return;
        }
      }
      const image = await depsRef.current.preparePhotoImage(photo.file, photo.rotation);
      if (!isCurrent()) {
        return;
      }
      const engine = await getEngine();
      if (!isCurrent()) {
        return;
      }
      const text = await engine.read(image);
      if (isCurrent()) {
        updatePhoto(job.id, { status: 'ready', text, error: undefined });
      }
    } catch (thrown) {
      // A stale read, including one whose engine was terminated under it, changes nothing.
      if (isCurrent()) {
        const code = thrown instanceof MaterialPrepError ? thrown.code : 'unknown';
        updatePhoto(job.id, { status: 'failed', error: code, text: undefined });
      }
    }
  };

  /** Starts queued reads while fewer than `OCR_WORKERS` are in flight (PHO-D9). */
  const pump = (scheduler: Scheduler) => {
    while (scheduler.inFlight < OCR_WORKERS && scheduler.queue.length > 0) {
      const job = scheduler.queue.shift() as ReadJob;
      if (tokensRef.current.get(job.id) !== job.token) {
        continue;
      }
      scheduler.inFlight++;
      void readPhoto(scheduler, job).finally(() => {
        scheduler.inFlight--;
        if (schedulerRef.current === scheduler) {
          pump(scheduler);
        }
      });
    }
  };

  /** Queues photos for reading, making any read of them already under way stale. */
  const enqueue = (ids: readonly string[]) => {
    const scheduler = schedulerRef.current;
    if (!scheduler) {
      return;
    }
    for (const id of ids) {
      const token = (tokensRef.current.get(id) ?? 0) + 1;
      tokensRef.current.set(id, token);
      scheduler.queue.push({ id, token });
    }
    // Started a microtask later, so a StrictMode double mount, which unmounts synchronously,
    // never decodes a photo for the mount it throws away.
    queueMicrotask(() => {
      if (schedulerRef.current === scheduler) {
        pump(scheduler);
      }
    });
  };

  // Each mount reads every photo still waiting, so none left by an earlier mount waits forever.
  // Its cleanup lets the engine go, so a StrictMode double mount neither leaks one nor reads twice.
  useEffect(() => {
    schedulerRef.current = { queue: [], inFlight: 0 };
    queuedLanguageRef.current = pageLanguageRef.current;
    enqueue(valueRef.current.photos.filter((photo) => photo.status === 'reading').map((p) => p.id));
    return () => {
      schedulerRef.current = undefined;
      releaseEngine();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A new page language reads every photo again on a new engine (PHO-D1, PHO-D10).
  useEffect(() => {
    if (queuedLanguageRef.current === pageLanguage) {
      return;
    }
    queuedLanguageRef.current = pageLanguage;
    releaseEngine();
    const current = valueRef.current;
    if (current.photos.length === 0) {
      return;
    }
    commit({ ...current, photos: current.photos.map(toReading) });
    enqueue(current.photos.map((photo) => photo.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLanguage]);

  // One object URL per photo in the set, revoked once it leaves the set.
  useEffect(() => {
    const urls = urlsRef.current;
    const ids = new Set(value.photos.map((photo) => photo.id));
    let changed = false;
    for (const [id, url] of urls) {
      if (!ids.has(id)) {
        URL.revokeObjectURL(url);
        urls.delete(id);
        changed = true;
      }
    }
    for (const photo of value.photos) {
      if (!urls.has(photo.id)) {
        urls.set(photo.id, URL.createObjectURL(photo.file));
        changed = true;
      }
    }
    if (changed) {
      setUrlsVersion((version) => version + 1);
    }
  }, [value.photos]);

  useEffect(
    () => () => {
      for (const url of urlsRef.current.values()) {
        URL.revokeObjectURL(url);
      }
      urlsRef.current.clear();
    },
    []
  );

  const handleAdd = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so adding the same files again fires `onChange`; `files` is captured first.
    e.target.value = '';
    if (files.length === 0) {
      return;
    }

    const accepted = files.filter(isAcceptedPhoto);
    const errors: MaterialPrepErrorCode[] =
      accepted.length < files.length ? ['PhotoUnsupported'] : [];
    const current = valueRef.current;
    if (current.photos.length + accepted.length > MAX_PHOTOS) {
      setAddErrors([...errors, 'TooManyPhotos']);
      return;
    }
    setAddErrors(errors);
    if (accepted.length === 0) {
      return;
    }

    const added = sortPhotosByName(
      accepted.map((file): Photo => ({
        id: crypto.randomUUID(),
        file,
        rotation: 0,
        status: 'reading',
      }))
    );
    commit({ ...current, photos: [...current.photos, ...added] });
    enqueue(added.map((photo) => photo.id));
  };

  /** Swaps photo `index` with its neighbour; renumbers only, reading nothing again (PHO-D4). */
  const move = (index: number, by: -1 | 1) => {
    const current = valueRef.current;
    const photos = [...current.photos];
    [photos[index], photos[index + by]] = [photos[index + by], photos[index]];
    commit({ ...current, photos });
  };

  /** Turns a photo a further 90° and reads that photo alone again (PHO-D3). */
  const rotate = (id: string) => {
    const current = valueRef.current;
    commit({
      ...current,
      photos: current.photos.map((photo) =>
        photo.id === id
          ? toReading({ ...photo, rotation: ((photo.rotation + 90) % 360) as PhotoRotation })
          : photo
      ),
    });
    enqueue([id]);
  };

  const remove = (id: string) => {
    const current = valueRef.current;
    tokensRef.current.delete(id);
    setAddErrors([]);
    if (selectedId === id) {
      setSelectedId(undefined);
    }
    commit({ ...current, photos: current.photos.filter((photo) => photo.id !== id) });
  };

  const numberFormat = new Intl.NumberFormat(i18n.language);
  const chars = photoSetChars(value);
  // Every photo read and still no text: say so, since Start stays disabled for it.
  const emptySet =
    value.photos.length > 0 &&
    value.photos.every((photo) => photo.status === 'ready') &&
    chars === 0;
  const materialErrors = [
    ...addErrors.map((code) => t(`error.${code}`)),
    chars > MATERIAL_MAX_CHARS ? t('error.MaterialTooLarge') : undefined,
    emptySet ? t('error.MaterialEmpty') : undefined,
    error,
  ].filter((message, index, all): message is string => !!message && all.indexOf(message) === index);
  const materialHasError = materialErrors.length > 0;

  const photoStatus = (photo: Photo) =>
    photo.status === 'reading'
      ? t('job.new.photos.reading')
      : photo.status === 'failed'
        ? t(`error.${photo.error ?? 'unknown'}`)
        : t('job.new.photos.chars', { chars: numberFormat.format(photo.text?.length ?? 0) });

  const selectedIndex = value.photos.findIndex((photo) => photo.id === selectedId);
  const selected = selectedIndex >= 0 ? value.photos[selectedIndex] : undefined;

  return (
    <>
      <div>
        <label htmlFor="job-page-language" className="form-label">
          {t('job.new.pageLanguage')}
        </label>
        <select
          id="job-page-language"
          className="form-select"
          aria-describedby="job-page-language-help"
          disabled={disabled}
          value={pageLanguage}
          onChange={(e) => onPageLanguageChange(e.target.value as Language)}
        >
          {LANGUAGES.map((code) => (
            <option key={code} value={code}>
              {t(`task.language.${code}`)}
            </option>
          ))}
        </select>
        <div id="job-page-language-help" className="form-text">
          {t('job.new.pageLanguageHelp')}
        </div>
      </div>

      <div>
        <div className="qz-photos">
          {value.photos.map((photo, index) => {
            const badge = t('job.new.photos.badge', { number: index + 1 });
            const url = urlsRef.current.get(photo.id);
            return (
              <div key={photo.id} className="qz-photo">
                <button
                  type="button"
                  className={`qz-photo-thumb${photo.id === selectedId ? ' is-selected' : ''}`}
                  aria-pressed={photo.id === selectedId}
                  onClick={() => setSelectedId(photo.id === selectedId ? undefined : photo.id)}
                >
                  {url && (
                    <img
                      src={url}
                      alt={badge}
                      style={{ transform: `rotate(${photo.rotation}deg)` }}
                    />
                  )}
                  <span className="qz-photo-badge" aria-hidden="true">
                    {badge}
                  </span>
                </button>
                <div
                  className={`qz-photo-status${photo.status === 'failed' ? ' text-danger' : ''}`}
                >
                  {photoStatus(photo)}
                </div>
                <div className="qz-photo-actions" role="group" aria-label={badge}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    aria-label={t('job.new.photos.earlier')}
                    title={t('job.new.photos.earlier')}
                    disabled={disabled || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <Icon name="arrow-left" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    aria-label={t('job.new.photos.later')}
                    title={t('job.new.photos.later')}
                    disabled={disabled || index === value.photos.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <Icon name="arrow-right" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    aria-label={t('job.new.photos.rotate')}
                    title={t('job.new.photos.rotate')}
                    disabled={disabled}
                    onClick={() => rotate(photo.id)}
                  >
                    <Icon name="rotate-right" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    aria-label={t('job.new.photos.remove')}
                    title={t('job.new.photos.remove')}
                    disabled={disabled}
                    onClick={() => remove(photo.id)}
                  >
                    <Icon name="xmark" />
                  </button>
                </div>
              </div>
            );
          })}
          <div
            className={`qz-dropzone is-split${disabled ? ' is-disabled' : ''}${
              materialHasError ? ' is-invalid' : ''
            }`}
          >
            {/* `capture` opens a phone's back camera; a picker that allows `multiple` opens
                Android's photo picker, which has no camera. One photo per tap. */}
            <label htmlFor="job-material-camera">
              <input
                type="file"
                id="job-material-camera"
                className="visually-hidden"
                accept="image/*"
                capture="environment"
                aria-describedby={materialHasError ? 'job-material-error' : undefined}
                disabled={disabled}
                onChange={handleAdd}
              />
              <Icon name="camera" />
              <span className="qz-small">{t('job.new.photos.take')}</span>
            </label>
            <label htmlFor="job-material">
              <input
                type="file"
                id="job-material"
                className="visually-hidden"
                accept={PHOTO_ACCEPT}
                multiple
                aria-describedby={materialHasError ? 'job-material-error' : undefined}
                disabled={disabled}
                onChange={handleAdd}
              />
              <Icon name="plus" />
              <span className="qz-small">{t('job.new.photos.add')}</span>
            </label>
          </div>
        </div>
        <div className="form-text">{t('job.new.photos.help')}</div>
        {value.photos.length > 0 && (
          <div className="qz-small qz-muted mt-1">
            {t('job.new.photos.summary', {
              count: value.photos.length,
              chars: numberFormat.format(chars),
            })}
          </div>
        )}
        {materialHasError && (
          <div id="job-material-error" className="invalid-feedback d-block">
            {materialErrors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div>
          <div className="form-label">
            {t('job.new.photos.preview', { number: selectedIndex + 1 })}
          </div>
          {selected.status === 'reading' ? (
            <p className="qz-small qz-muted">{t('job.new.photos.reading')}</p>
          ) : selected.status === 'failed' ? (
            <div className="invalid-feedback d-block">
              {t(`error.${selected.error ?? 'unknown'}`)}
            </div>
          ) : (selected.text ?? '').trim() === '' ? (
            <p className="qz-small qz-muted">{t('job.new.pdf.previewEmpty')}</p>
          ) : (
            <div
              className="border rounded p-2 overflow-auto"
              style={{ maxHeight: '20rem', whiteSpace: 'pre-wrap' }}
            >
              {selected.text}
            </div>
          )}
        </div>
      )}
    </>
  );
};
